import { translateVoucherMessage } from '~~/shared/utils/voucherMessages'

// Wyciąga komunikat błędu z odpowiedzi REST do pokazania użytkownikowi.
// Konkretny komunikat z PrestaShop trafia do err.data.statusMessage (potwierdzone na 422).
// Pomijamy świadomie:
//  - err.message z ofetch → brzydki prefiks `[POST] "url": 422`,
//  - top-level err.statusMessage → generyczne „Unprocessable Entity" (HTTP reason phrase).
// Gdy body nie niesie treści → podany polski fallback.
export function extractApiErrorMessage(error: unknown, fallback: string): string {
  const err = error as { data?: { statusMessage?: string; message?: string } } | null
  return err?.data?.statusMessage || err?.data?.message || fallback
}

export interface ExistingOrderRef {
  idOrder: number
  secureKey: string
  reference: string
}

/**
 * Wykrywa sygnał „zamówienie już istnieje dla tego koszyka". Backend (contelizerrest
 * placeOrder) przy Cart::OrderExists rzuca ConflictException(409) z details
 * { id_order, secure_key, reference }. Pozwala frontowi skierować gościa do ekranu
 * zamówienia / dokończenia płatności (thank-you z order_id+key) zamiast pokazywać
 * generyczny błąd i zostawiać skonsumowany koszyk. Zwraca null, gdy to nie jest
 * 409-conflict z identyfikatorem zamówienia. Przeszukuje wszystkie poziomy
 * zagnieżdżenia błędu (data / data.data / response._data / response._data.data).
 */
export function extractExistingOrder(error: unknown): ExistingOrderRef | null {
  if (backendStatusCode(error) !== 409) return null
  for (const map of collectErrorMaps(error)) {
    const idOrder = Number(map.id_order)
    if (Number.isFinite(idOrder) && idOrder > 0) {
      return {
        idOrder,
        secureKey: typeof map.secure_key === 'string' ? map.secure_key : '',
        reference: typeof map.reference === 'string' ? map.reference : '',
      }
    }
  }
  return null
}

// ── Tłumaczenie błędów backendu na polski (field-level) ──────────────────────
// contelizerrest rzuca ValidationException po ANGIELSKU z mapą pól, np.
// { statusMessage: "Invalid billing postcode.", data: { "billing.zip": [...] } }.
// translateBackendFieldErrors klasyfikuje błędy po KLUCZU pola (locale-niezależnie)
// na token kanoniczny + polski komunikat i rozdziela na fieldErrors vs general.
// Użytkownik NIGDY nie widzi surowego angielskiego stringa ani prefiksu `field:`.

const GENERIC_BACKEND_FALLBACK = 'Nie udało się przetworzyć żądania. Sprawdź wprowadzone dane i spróbuj ponownie.'

/**
 * Komunikat dla 401 z dowolnego endpointu sklepu.
 *
 * Dlaczego osobno: backend chroni cały checkout (paymentoptions, setaddresscheckout,
 * setcarriercheckout, address, alladdresses) i przy wygasłej sesji odpowiada 401.
 * Bez tej gałęzi 401 spadało do GENERIC_BACKEND_FALLBACK, czyli klient dostawał
 * „Sprawdź wprowadzone dane" — instrukcję, której wykonanie NICZEGO nie zmienia.
 */
export const SESSION_EXPIRED_MESSAGE = 'Twoja sesja wygasła. Zaloguj się ponownie, aby dokończyć zamówienie — zawartość koszyka zostaje zachowana.'

/** 503 przy weryfikacji punktu InPost International — punkt może być dobry, API chwilowo nie odpowiada. */
export const PICKUP_POINT_RETRY_MESSAGE = 'Nie udało się teraz potwierdzić punktu odbioru w InPost. Wybierz go ponownie za chwilę.'

/** Każde inne 503 — usługa po drugiej stronie chwilowo niedostępna. */
export const SERVICE_RETRY_MESSAGE = 'Usługa jest chwilowo niedostępna. Spróbuj ponownie za chwilę.'

/** Czy błąd backendu to odmowa tożsamości (401), a nie problem z danymi formularza. */
export function isSessionExpiredError(error: unknown): boolean {
  return backendStatusCode(error) === 401
}

export interface BackendFieldError {
  /** Token kanoniczny pola, opcjonalnie z prefiksem strony: "billing.postalCode", "email". */
  field: string
  /** Polski komunikat dla użytkownika. */
  message: string
}

export interface TranslatedBackend {
  fieldErrors: BackendFieldError[]
  general: string[]
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined
}

/** „billing" → „do faktury", „shipping" → „dostawy". */
function addressSidePl(side: string | null): string {
  if (!side) return ''
  return side === 'billing' ? ' w adresie do faktury' : ' w adresie dostawy'
}

// `[POST] "/api/store/checkout/prepare": 403 Forbidden` — prefiks ofetch dokleja
// metodę i URL, a nie treść backendu. Nigdy nie jest komunikatem do rozpoznania.
const OFETCH_MESSAGE_RE = /^\[[A-Z]+\]\s/

/**
 * Komunikat backendu, czytany ze WSZYSTKICH poziomów zagnieżdżenia — dokładnie
 * tych samych, których używa backendStatusCode().
 *
 * Na wierzchu błędu ofetch stawia `statusMessage` z REASON PHRASE odpowiedzi
 * („Conflict", „Forbidden"), a w HTTP/2 nie ma go w ogóle. Treść od backendu Nitro
 * pakuje do ciała (`data.statusMessage`), które bywa podwójnie zagnieżdżone
 * (`data.data`) albo siedzi w surowym `response._data`. Czytanie samego wierzchu
 * robiło martwymi WSZYSTKIE gałęzie rozpoznające powód po treści — klient dostawał
 * „Sprawdź wprowadzone dane", choć backend podał konkretny powód. Ciało ma więc
 * priorytet, a wierzch jest ostatnim kandydatem.
 */
function backendStatusMessage(error: unknown): string {
  const rec = asRecord(error)
  const candidates: unknown[] = []
  for (const map of collectErrorMaps(error)) candidates.push(map.statusMessage, map.message)
  candidates.push(rec?.statusMessage, rec?.message)

  for (const candidate of candidates) {
    if (typeof candidate !== 'string') continue
    const text = candidate.trim()
    if (!text || OFETCH_MESSAGE_RE.test(text)) continue
    return text
  }

  return ''
}

/**
 * Kod HTTP błędu backendu, odporny na wszystkie trzy kształty, w jakich do nas dociera:
 *  - `err.statusCode` — ofetch stawia go z realnego statusu odpowiedzi,
 *  - `err.data.statusCode` — echo z ciała, które Nitro buduje przez createError()
 *    (tak czyta go m.in. useStoreApi przy wishliście),
 *  - `err.response.status` — surowy FetchError, gdy błąd przeszedł przez inny wrapper.
 * Czytanie tylko pierwszego z nich cicho gubiło 401 tam, gdzie w górę szło samo ciało.
 */
export function backendStatusCode(error: unknown): number {
  const rec = asRecord(error)
  if (typeof rec?.statusCode === 'number') return rec.statusCode

  const data = asRecord(rec?.data)
  if (typeof data?.statusCode === 'number') return data.statusCode

  const response = asRecord(rec?.response)
  if (typeof response?.status === 'number') return response.status

  return 0
}

/**
 * Zbiera mapy-kandydatki ze WSZYSTKICH możliwych poziomów zagnieżdżenia:
 *  - error.data (kształt serwerowy createError)
 *  - error.data.data (REALNY kształt klienta — koperta trasy Nuxt)
 *  - error.response._data oraz error.response._data.data (surowy FetchError)
 * Z map wyciągamy tylko klucze rozpoznane jako pola (junk ignorowany przez classifyFieldKey).
 */
function collectErrorMaps(error: unknown): Record<string, unknown>[] {
  const rec = asRecord(error)
  const maps: Record<string, unknown>[] = []
  const d1 = asRecord(rec?.data)
  if (d1) { maps.push(d1); const d2 = asRecord(d1.data); if (d2) maps.push(d2) }
  const resp = asRecord(asRecord(rec?.response)?._data)
  if (resp) { maps.push(resp); const rd = asRecord(resp.data); if (rd) maps.push(rd) }
  return maps
}

function detailEntries(error: unknown): Array<{ key: string, message: string }> {
  const maps = collectErrorMaps(error)

  const out: Array<{ key: string, message: string }> = []
  for (const map of maps) {
    for (const [key, value] of Object.entries(map)) {
      if (typeof value === 'string' && value.trim()) out.push({ key, message: value })
      else if (Array.isArray(value)) {
        for (const entry of value) {
          if (typeof entry === 'string' && entry.trim()) out.push({ key, message: entry })
        }
      }
    }
  }
  return out
}

/**
 * Nazwy produktów, których nie da się kupić — z `details.products` błędu 409.
 * Backend wysyła tam listę obiektów (id_product, name, reason), bo pojedyncze
 * „coś jest niedostępne" nie pozwalało klientowi ustalić, co usunąć z koszyka.
 */
function unavailableProductNames(error: unknown): string[] {
  const names: string[] = []

  for (const map of collectErrorMaps(error)) {
    const products = map.products
    if (!Array.isArray(products)) continue

    for (const entry of products) {
      const record = asRecord(entry)
      const name = typeof record?.name === 'string' ? record.name.trim() : ''
      if (name) names.push(name)
    }
  }

  return [...new Set(names)]
}

function unavailableProductsMessage(error: unknown): string {
  const names = unavailableProductNames(error)
  const tail = ' Odśwież koszyk — przeniesiemy je do sekcji „Wyprzedane", a pozostałe produkty zamówisz normalnie.'

  if (!names.length) {
    return 'Jeden z produktów w koszyku właśnie się wyprzedał.' + tail
  }

  if (names.length === 1) {
    return `Produkt „${names[0]}" właśnie się wyprzedał.` + tail
  }

  return `Te produkty właśnie się wyprzedały: ${names.join(', ')}.` + tail
}

/** Klucz pola backendu → { side, token } lub null gdy nierozpoznany (junk). */
function classifyFieldKey(key: string): { side: string | null, token: string } | null {
  const lower = key.toLowerCase()
  const side = lower.startsWith('billing.') ? 'billing' : lower.startsWith('shipping.') ? 'shipping' : null
  const bare = lower.replace(/^(billing|shipping)\./, '')
  if (bare === 'zip' || bare === 'postcode' || bare === 'postal_code') return { side, token: 'postalCode' }
  if (bare === 'country_code' || bare === 'country') return { side, token: 'country' }
  if (bare === 'city') return { side, token: 'city' }
  if (bare === 'address1' || bare === 'street') return { side, token: 'street' }
  // Numer domu ma już własny input (checkout i książka adresowa) — backend odrzuca
  // adres bez numeru z kluczem `address2`, więc podświetlamy właśnie to pole.
  if (bare === 'address2' || bare === 'building') return { side, token: 'building' }
  if (bare === 'email') return { side, token: 'email' }
  if (bare === 'new_password') return { side, token: 'newPassword' }
  if (bare === 'password') return { side, token: 'password' }
  // Backend adresowy wysyła klucze payloadu (`first_name`), a nie kolumny Address
  // (`firstname`) — bez tych aliasów błąd imienia/nazwiska z /rest/checkoutprepare
  // był po cichu gubiony.
  if (bare === 'firstname' || bare === 'lastname' || bare === 'first_name' || bare === 'last_name') {
    return { side, token: 'fullName' }
  }
  // Telefon i NIP mają w checkoucie po JEDNYM inpucie w grupie „dane klienta" —
  // stronę adresu celowo zerujemy, bo `billing.phone`/`shipping.phone` nie mają slotu
  // i komunikat by nie wyrenderował się wcale.
  if (bare === 'phone' || bare === 'phone_mobile') return { side: null, token: 'phone' }
  if (bare === 'vat_number' || bare === 'vatnumber' || bare === 'nip') return { side: null, token: 'taxId' }
  return null
}

/**
 * Pola, które backend potrafi odrzucić, a checkout nie ma dla nich osobnego inputu.
 * Zwraca gotowy polski komunikat do bannera albo null, gdy klucza nie rozpoznajemy
 * (wtedy zostaje ogólny fallback — lepszy niż mylące wskazanie złego pola).
 */
function describeUnanchoredField(key: string): string | null {
  const lower = key.toLowerCase()
  const side = lower.startsWith('billing.') ? 'billing' : lower.startsWith('shipping.') ? 'shipping' : null
  const bare = lower.replace(/^(billing|shipping)\./, '')
  const where = addressSidePl(side)

  if (bare === 'company') return `Sprawdź nazwę firmy${where}.`
  // Punkt odbioru InPost International: brak punktu przy składaniu zamówienia
  // (`shipping.pickup_point` z InpostInternationalCheckoutGuard) albo punkt odrzucony przy
  // zapisie (`point` — nie istnieje, nie przyjmuje paczek, leży w innym kraju niż adres).
  if (bare === 'pickup_point' || bare === 'point') {
    return 'Wybierz punkt odbioru na mapie — musi leżeć w kraju adresu dostawy.'
  }
  return null
}

// Token formatu z detalu jest SUROWY (`%zipcode%`, np. „NN-NNN"); bierzemy
// pierwszy ciąg w cudzysłowie i humanizujemy do przykładu „00-000".
const QUOTED_TOKEN_RE = /["“]\s*([^"”]+?)\s*["”]/
function humanizeFormatToken(token: string): string {
  return token.replace(/N/g, '0').replace(/L/g, 'A').replace(/C/g, '')
}

/** Polski komunikat dla danego tokenu (+ ewentualny format z surowego detalu). */
function messageForToken(token: string, side: string | null, rawDetail: string): string {
  const where = addressSidePl(side)
  switch (token) {
    case 'postalCode': {
      const m = QUOTED_TOKEN_RE.exec(rawDetail)
      const fmt = m?.[1] ? ` (oczekiwany format: ${humanizeFormatToken(m[1].trim())})` : ''
      return `Nieprawidłowy kod pocztowy${where}${fmt}.`
    }
    case 'country':
      return `Nieobsługiwany kraj${where}. Wybierz kraj z listy.`
    case 'city':
      return `Podaj miasto${where}.`
    case 'street':
      return `Podaj ulicę${where}.`
    case 'building':
      return `Sprawdź numer domu lub lokalu${where}.`
    case 'email':
      return 'Podaj poprawny adres e-mail.'
    case 'newPassword':
      return 'Nowe hasło nie spełnia wymagań — użyj dłuższego i mocniejszego hasła.'
    case 'password':
      return 'Podaj aktualne hasło, aby zmienić e-mail lub hasło.'
    case 'fullName':
      return 'Sprawdź poprawność imienia i nazwiska — bez cyfr i znaków specjalnych.'
    case 'phone':
      // PrestaShop waliduje telefon regexem [+0-9. ()/-], więc litery (np. „wew.")
      // wywalają zapis adresu. Mówimy wprost, co jest dozwolone.
      return 'Nieprawidłowy numer telefonu. Użyj cyfr — dozwolone są też znaki + ( ) - . i spacje.'
    case 'taxId':
      return 'Sprawdź numer NIP.'
    default:
      return `Sprawdź poprawność pola${where}.`
  }
}

/**
 * Mapuje błąd backendu na strukturę { fieldErrors, general }. fieldErrors są
 * kluczowane po tokenie kanonicznym (z prefiksem strony), general to komunikaty
 * nie-polowe (banner). Zawsze bez surowego angielskiego.
 */
export function translateBackendFieldErrors(error: unknown, fallback: string = GENERIC_BACKEND_FALLBACK): TranslatedBackend {
  const statusCode = backendStatusCode(error)
  const statusMessage = backendStatusMessage(error)
  const entries = detailEntries(error)

  // 401 rozstrzyga się PIERWSZY i nigdy nie schodzi do fallbacku „sprawdź dane".
  // Backend odmawia tożsamości, a nie treści formularza — klient mógłby poprawiać
  // adres w nieskończoność i nic by to nie dało. Zob. isSessionExpiredError().
  if (statusCode === 401) {
    return { fieldErrors: [], general: [SESSION_EXPIRED_MESSAGE] }
  }

  // 503 = chwilowa niedostępność (np. API InPostu przy weryfikacji punktu odbioru, z detalem
  // `retryable`), a NIE błąd danych. Musi wyprzedzić klasyfikację pól: klucz `point` /
  // `shipping.pickup_point` trafiłby w komunikat „punkt musi leżeć w kraju adresu dostawy",
  // a klient szukałby błędu w poprawnym wyborze. Rozpoznajemy po samym kodzie — detale są
  // tylko doprecyzowaniem.
  if (statusCode === 503) {
    const aboutPickupPoint = entries.some(entry => /(^|\.)(pickup_point|point)$/i.test(entry.key))
    return { fieldErrors: [], general: [aboutPickupPoint ? PICKUP_POINT_RETRY_MESSAGE : SERVICE_RETRY_MESSAGE] }
  }

  if (statusCode === 422 && /^(?:no active cart in session|cart is not initialized|cookie is not set)\.?$/i.test(statusMessage)) {
    return {
      fieldErrors: [],
      general: ['Nie udało się odtworzyć sesji koszyka. Wróć do koszyka i odśwież stronę przed ponowną próbą płatności.'],
    }
  }

  // Płatność niedostępna dla zamówienia (zachowane zachowanie z checkoutu).
  if (statusCode === 403 && /payment method/i.test(statusMessage)) {
    return { fieldErrors: [], general: ['Płatność przy odbiorze nie jest dostępna dla tego zamówienia.'] }
  }

  // Produkt wyprzedał się między obejrzeniem koszyka a zapłatą (409 z bramki
  // ProductExistenceGuard). Nazwy są w szczegółach jako TABLICA OBIEKTÓW, więc
  // generyczna klasyfikacja po kluczach ich nie widzi i błąd wpadałby w fallback
  // „Sprawdź dane" — czyli klient znowu nie wiedziałby, czego nie może kupić.
  if (/no longer available/i.test(statusMessage)) {
    return { fieldErrors: [], general: [unavailableProductsMessage(error)] }
  }

  // Domenowe przypadki konta (accountedit) — rozpoznawane po statusMessage, NIE po polu.
  // Muszą wyprzedzać generyczną klasyfikację klucza, by e-mail zajęty wygrał z formatowym
  // „Podaj poprawny adres e-mail." (klucz email pojawia się tu mimo poprawnego formatu).
  // Wąskie regexy — kontekst hasła/e-maila, nie trafiają na inne komunikaty
  // (np. „incorrect format" gdzie indziej nie zostanie zmapowane na hasło).
  if (/password.*incorrect|incorrect.*password/i.test(statusMessage)) {
    return { fieldErrors: [{ field: 'password', message: 'Nieprawidłowe aktualne hasło.' }], general: [] }
  }
  if (/email/i.test(statusMessage) && /(already|used)/i.test(statusMessage)) {
    return { fieldErrors: [{ field: 'email', message: 'Ten adres e-mail jest już używany.' }], general: [] }
  }

  // Field-level: klasyfikacja po KLUCZU. Dedupe po polu (pierwszy detal wygrywa).
  const seen = new Set<string>()
  const fieldErrors: BackendFieldError[] = []
  // Pola, których checkout NIE ma jako osobnego inputu (formularz renderuje tylko
  // street/building/postalCode/city/country + fullName/phone/taxId). Kiedyś takie błędy
  // wypadały z pętli przez `continue` i przepadały BEZ ŚLADU — a skoro niżej
  // `general` jest zerowane, gdy cokolwiek innego się sklasyfikowało, klient mógł
  // nie zobaczyć nic. Nazywamy więc pole i kierujemy komunikat do bannera.
  const unanchored: string[] = []
  for (const entry of entries) {
    const cls = classifyFieldKey(entry.key)
    if (!cls) {
      const named = describeUnanchoredField(entry.key)
      if (named) unanchored.push(named)
      continue
    }
    const field = cls.side ? `${cls.side}.${cls.token}` : cls.token
    if (seen.has(field)) continue
    seen.add(field)
    fieldErrors.push({ field, message: messageForToken(cls.token, cls.side, entry.message) })
  }

  if (fieldErrors.length || unanchored.length) {
    return { fieldErrors, general: [...new Set(unanchored)] }
  }

  // Brak rozpoznanych pól — synteza z angielskiego statusMessage (gdy backend nie
  // dołączył szczegółów). Mapujemy na field, by UI mógł podświetlić pole.
  const postcode = /invalid (billing|shipping) postcode/i.exec(statusMessage)
  if (postcode) {
    const side = postcode[1]!.toLowerCase()
    return { fieldErrors: [{ field: `${side}.postalCode`, message: messageForToken('postalCode', side, '') }], general: [] }
  }
  const country = /invalid (billing|shipping) country code/i.exec(statusMessage)
  if (country) {
    const side = country[1]!.toLowerCase()
    return { fieldErrors: [{ field: `${side}.country`, message: messageForToken('country', side, '') }], general: [] }
  }
  const validation = /(billing|shipping) address validation failed/i.exec(statusMessage)
  if (validation) {
    const side = validation[1]!.toLowerCase()
    return { fieldErrors: [], general: [`Uzupełnij wszystkie wymagane pola${addressSidePl(side)}.`] }
  }

  // Nic nie rozpoznano → ogólny polski fallback (surowy angielski NIE wycieka).
  return { fieldErrors: [], general: [fallback] }
}

/** Spłaszcza fieldErrors do mapy token→komunikat (gubi prefiks strony billing./shipping.). */
export function toFieldErrorMap(fieldErrors: BackendFieldError[]): Record<string, string> {
  const map: Record<string, string> = {}
  for (const fe of fieldErrors) map[fe.field.includes('.') ? fe.field.split('.')[1]! : fe.field] = fe.message
  return map
}

/**
 * Legacy banner: spłaszcza fieldErrors+general do listy stringów. Zachowuje
 * dotychczasową sygnaturę i zachowanie dla callerów pokazujących sam banner.
 */
export function translateBackendError(error: unknown, fallback: string = GENERIC_BACKEND_FALLBACK): string[] {
  const { fieldErrors, general } = translateBackendFieldErrors(error, fallback)
  const messages = [...general, ...fieldErrors.map(e => e.message)]
  return messages.length ? [...new Set(messages)] : [fallback]
}

// ── Tłumaczenie komunikatów /checkoutvalidate ───────────────────────────────
// checkoutvalidate zwraca angielskie komunikaty domenowe (koszyk/stan/adres/
// przewoźnik/płatność). Komunikaty kuponów (CartRule) też wracają po angielsku —
// tłumaczy je translateVoucherMessage; nierozpoznane przechodzą w oryginale.
const CHECKOUT_VALIDATION_MESSAGES: Array<[RegExp, string]> = [
  [/cart is not initialized/i, 'Koszyk nie został zainicjowany.'],
  [/cart is empty/i, 'Koszyk jest pusty.'],
  [/no longer available in requested quantity/i, 'Niektóre produkty są niedostępne w żądanej ilości. Odśwież koszyk — pokażemy, które to pozycje.'],
  // 409 z bramki płatności (ProductExistenceGuard): stan zszedł do zera między
  // obejrzeniem koszyka a zapłatą. Koszyk po odświeżeniu przeniesie taką pozycję
  // do sekcji „Wyprzedane", więc kierujemy klienta właśnie tam.
  [/product in your cart is no longer available/i, 'Jeden z produktów w koszyku właśnie się wyprzedał. Odśwież koszyk — przeniesiemy go do sekcji „Wyprzedane", a pozostałe produkty zamówisz normalnie.'],
  [/product is still unavailable/i, 'Ten produkt nadal jest niedostępny.'],
  [/cart does not contain purchasable products/i, 'W koszyku nie ma produktów, które można zamówić.'],
  [/requires minimum quantity/i, 'Jeden z produktów wymaga większej minimalnej ilości.'],
  [/delivery address is required/i, 'Wybierz adres dostawy.'],
  [/invoice address is required/i, 'Wybierz adres do faktury.'],
  [/carrier is required/i, 'Wybierz metodę dostawy.'],
  [/no payment options are currently available/i, 'Brak dostępnych metod płatności.'],
  [/payment method is not available for the chosen carrier/i, 'Wybrana metoda płatności nie jest dostępna dla wybranego sposobu dostawy.'],
  [/coupon code does not exist/i, 'Podany kod rabatowy nie istnieje.'],
  [/order total is zero because the cart is empty/i, 'Suma zamówienia wynosi zero — koszyk jest pusty.'],
]

/**
 * Tłumaczy pojedynczy komunikat z /checkoutvalidate na polski. `field` pozwala
 * przepuścić zlokalizowane komunikaty kuponów bez zmian; nierozpoznane (i nie-
 * kuponowe) → ogólny polski komunikat, żeby surowy angielski nie wyciekł.
 */
export function translateCheckoutMessage(message: string, field?: string): string {
  const trimmed = (message || '').trim()
  if (!trimmed) return ''

  for (const [pattern, polish] of CHECKOUT_VALIDATION_MESSAGES) {
    if (pattern.test(trimmed)) return polish
  }

  // Kupony: PrestaShop zwraca komunikaty CartRule po angielsku (domena
  // Shop.Notifications.Error bez tłumaczeń w kontekście REST) — tłumaczymy je
  // wspólnym translatorem; nierozpoznany / już-polski string przechodzi bez zmian.
  if (field === 'coupons' || field === 'coupon_code') return translateVoucherMessage(trimmed)

  return 'Nie udało się zweryfikować zamówienia. Sprawdź dane i spróbuj ponownie.'
}
